'use client'

/**
 * SiteAccessGate — site-wide access model (rebuilt 2026-07-12 for growth/SEO).
 *
 * MODEL: default-OPEN. Content pages (tickers, dashboards, movers, scanners, recaps, feeds) are
 * fully readable by anonymous first-time visitors — no wall, no timer. This is deliberate: walling
 * an organic search visitor pogo-sticks them back to Google, which wastes the visit AND signals low
 * quality (short dwell time), suppressing rankings. Content isn't the moat; the interactive/personal
 * layer is. So we gate on ACTION, not time:
 *
 *   • ACCOUNT_GATED routes  → require any signed-in account (personal pages useless without one).
 *                             Anonymous sees a "create free account" wall — caught at the exact
 *                             moment they try to DO something, the highest-intent conversion point.
 *   • PREMIUM_GATED routes  → require premium (live realtime surfaces). Non-premium → premium wall.
 *   • Everything else       → OPEN. Anonymous readers get a DISMISSIBLE nudge after a few pages.
 *
 * /chart and /alerts are intentionally NOT gated here — they carry their own richer, free-tier-aware
 * PremiumContentGate. Double-walling would replace its nicer freemium placeholder with our blunt one.
 *
 * Incognito can't be "exploited": content is free by design, and gated features require a server-side
 * account (an incognito window has no saved watchlist/alerts to exploit). The 5 free premium days are
 * granted per verified email, so email verification is the anti-abuse layer — not browser tricks.
 *
 * Admins and ?embed=1 (OG / reddit chart embeds) are never walled or nudged.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useSession, signIn } from 'next-auth/react'
import { usePathname } from 'next/navigation'
import Link from 'next/link'
import { Sprout, Clock, ArrowRight, Check, X, TrendingUp } from 'lucide-react'
import { PRICING, formatUSD } from '@/lib/pricing'
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext'
import { guestNudgeClaimed, claimGuestNudge } from '@/lib/guestNudge'

const FETCH_TIMEOUT_MS = 4_000
const ACCESS_CACHE = 'dt_access_gate'
const CACHE_TTL_MS = 5 * 60 * 1000
const VIEW_COUNT_KEY = 'dt_pv'              // anonymous open-content pageview counter (per session)
const NUDGE_DISMISSED_KEY = 'dt_nudge_dismissed'
const NUDGE_AFTER_VIEWS = 6                 // show the soft nudge once the reader is clearly engaged

// Routes that REQUIRE a signed-in account (any tier). Personal / useless without an account.
// Anonymous → "create free account" wall. Any signed-in user passes.
const ACCOUNT_GATED = ['/messages', '/settings', '/game', '/trading', '/trades']
// Routes that REQUIRE premium (live realtime surfaces). Non-premium signed-in → premium wall.
const PREMIUM_GATED = ['/stream']

type Kind = 'open' | 'account' | 'premium'
function under(pathname: string, prefixes: string[]): boolean {
  return prefixes.some((p) => pathname === p || pathname.startsWith(p + '/'))
}
function classify(pathname: string): Kind {
  if (under(pathname, PREMIUM_GATED)) return 'premium'
  if (under(pathname, ACCOUNT_GATED)) return 'account'
  return 'open'
}
// Ticker / chart pages are owned by GuestEngagementNudge (the symbol-specific, higher-intent card).
// This generic nudge defers there so the two never compete on the same page.
function isTickerPath(pathname: string): boolean {
  return /^\/(?:stocks|chart)(?:\/|$)/i.test(pathname)
}

type Access = { hasAccess: boolean } | null

export default function SiteAccessGate() {
  const pathname = usePathname() ?? '/'
  const { data: session, status } = useSession()
  const [access, setAccess] = useState<Access>(null)
  const [embed, setEmbed] = useState(false)
  const [views, setViews] = useState(0)
  const [nudgeDismissed, setNudgeDismissed] = useState(false)
  const [claimedElsewhere, setClaimedElsewhere] = useState(false)

  const kind = useMemo(() => classify(pathname), [pathname])
  const isAdmin = session?.user?.isAdmin === true

  // Embed views (OG / reddit chart embeds) are public — never wall or nudge.
  useEffect(() => {
    try { setEmbed(new URLSearchParams(window.location.search).get('embed') === '1') } catch { /* ssr */ }
  }, [pathname])

  // Remember a dismissed nudge for the rest of the session.
  useEffect(() => {
    try { setNudgeDismissed(sessionStorage.getItem(NUDGE_DISMISSED_KEY) === '1') } catch { /* ignore */ }
  }, [])

  // One guest nudge per session across surfaces: re-check the shared claim on each navigation, so if
  // GuestEngagementNudge (the ticker-page card) already fired, this generic one stands down.
  useEffect(() => {
    setClaimedElsewhere(guestNudgeClaimed())
  }, [pathname, views, status])

  // Count anonymous content pageviews so the soft nudge only fires for an engaged reader.
  useEffect(() => {
    if (embed || isAdmin || kind !== 'open' || status !== 'unauthenticated') return
    try {
      const n = (parseInt(sessionStorage.getItem(VIEW_COUNT_KEY) || '0', 10) || 0) + 1
      sessionStorage.setItem(VIEW_COUNT_KEY, String(n))
      setViews(n)
    } catch { /* ignore */ }
  }, [pathname, kind, status, embed, isAdmin])

  // Premium check — only needed on premium-gated routes for a signed-in user.
  useEffect(() => {
    if (kind !== 'premium' || status !== 'authenticated') return
    const email = session?.user?.email ?? ''
    try {
      const raw = sessionStorage.getItem(ACCESS_CACHE)
      if (raw) { const c = JSON.parse(raw); if (c.email === email && Date.now() - c.at < CACHE_TTL_MS) { setAccess({ hasAccess: c.hasAccess }); return } }
    } catch { /* ignore */ }
    const ctl = new AbortController()
    const to = setTimeout(() => ctl.abort(), FETCH_TIMEOUT_MS)
    fetch('/api/user/subscription-status', { signal: ctl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        const hasAccess = d?.hasAccess === true
        setAccess({ hasAccess })
        try { sessionStorage.setItem(ACCESS_CACHE, JSON.stringify({ email, hasAccess, at: Date.now() })) } catch { /* ignore */ }
      })
      .catch(() => setAccess({ hasAccess: true }))   // fail-open for signed-in users
      .finally(() => clearTimeout(to))
    return () => { clearTimeout(to); ctl.abort() }
  }, [kind, status, session?.user?.email])

  const variant = useMemo(() => {
    if (embed || isAdmin) return null                         // admins/owner + embeds never walled
    if (kind === 'account') return status === 'unauthenticated' ? ('signin' as const) : null
    if (kind === 'premium') {
      if (status === 'unauthenticated') return 'signin' as const
      if (status === 'authenticated' && access && !access.hasAccess) return 'premium' as const
      return null
    }
    return null                                               // open route → never walled
  }, [embed, isAdmin, kind, status, access])

  const walled = variant != null

  // Freeze the page behind a hard wall (account/premium routes only).
  useEffect(() => {
    if (!walled) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => { document.body.style.overflow = prev }
  }, [walled])

  const showNudge =
    !walled && !embed && !isAdmin &&
    kind === 'open' && status === 'unauthenticated' &&
    views >= NUDGE_AFTER_VIEWS && !nudgeDismissed &&
    !claimedElsewhere && !isTickerPath(pathname)

  // Claim the shared one-nudge-per-session slot the moment this card is about to show, so the
  // ticker-page nudge does not also appear on the next navigation.
  useEffect(() => { if (showNudge) claimGuestNudge() }, [showNudge])

  if (walled && variant) return <Wall variant={variant} />
  if (showNudge) {
    return (
      <Nudge onDismiss={() => {
        setNudgeDismissed(true)
        try { sessionStorage.setItem(NUDGE_DISMISSED_KEY, '1') } catch { /* ignore */ }
      }} />
    )
  }
  return null
}

/** Dismissible, non-blocking bottom card for engaged anonymous readers. */
function Nudge({ onDismiss }: { onDismiss: () => void }) {
  /** Server-fed: this is a client module and the flag has no NEXT_PUBLIC_ prefix, so a direct
   *  import renders one sentence on the server and another on hydration. */
  const { emailVerification } = useFeatureFlags()
  return (
    <div className="fixed inset-x-0 bottom-0 z-[55] flex justify-center px-3 pb-3 sm:pb-5">
      <div className="pointer-events-auto relative w-full max-w-lg overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-zinc-900 to-zinc-950 p-5 shadow-2xl shadow-black/60">
        <button type="button" onClick={onDismiss} aria-label="Dismiss" className="absolute right-3 top-3 rounded-lg p-1 text-zinc-500 transition-colors hover:text-zinc-300">
          <X className="h-4 w-4" />
        </button>
        <div className="pointer-events-none absolute -top-16 left-8 h-32 w-32 rounded-full bg-emerald-500/20 blur-3xl" />
        <div className="relative flex items-start gap-4">
          <div className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-emerald-500/15 ring-1 ring-emerald-500/30 sm:flex">
            <Sprout className="h-5 w-5 text-emerald-300" />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-base font-bold tracking-tight text-white">Enjoying the data? Make it yours.</h3>
            <p className="mt-1 text-[13px] leading-relaxed text-zinc-400">
              {/* ⚠ "get instant unusual-volume alerts" was audited 2026-08-09 and reworded. We do not
                  SEND unusual-volume alerts to anyone; we SHOW them live. See the claim audit in
                  PulseBoard.tsx. Reworded to a viewing claim, which is true and still the real draw. */}
              {/* ⛔ "start your 5 free premium days" overpromised: signupGrants.ts gives 1 day at
                  register and the 5-day block only after the email is VERIFIED, and 54% never do.
                  See `signup-promises-5-delivers-1`. */}
              Create a free account to build a watchlist, watch unusual-volume alerts fire live, and start your premium days.{emailVerification ? ' A full day now, 5 more when you verify your email.' : ''} No card needed.
            </p>
            <div className="mt-3 flex flex-col gap-2 sm:flex-row">
              <button
                type="button"
                onClick={() => signIn('google', { callbackUrl: typeof window !== 'undefined' ? window.location.href : '/' })}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-white px-4 py-2.5 text-sm font-bold text-zinc-900 transition-colors hover:bg-zinc-100"
              >
                <GoogleG /> Continue with Google
              </button>
              <Link prefetch={false} href="/auth?mode=signup" className="flex flex-1 items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-4 py-2.5 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/10">
                Sign up with email
              </Link>
            </div>
            <button type="button" onClick={onDismiss} className="mt-2 text-xs font-medium text-zinc-500 transition-colors hover:text-zinc-300">
              Maybe later
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

function Wall({ variant }: { variant: 'signin' | 'premium' }) {
  const premium = variant === 'premium'
  return (
    <div role="dialog" aria-modal="true" className="fixed inset-0 z-[60] flex items-center justify-center px-4">
      {/* Blurred, dimmed veil over the whole page — the value is visible but frozen behind it. */}
      <div className="absolute inset-0 bg-zinc-950/75 backdrop-blur-md" />

      <div className="relative w-full max-w-md overflow-hidden rounded-3xl border border-white/10 bg-gradient-to-b from-zinc-900 to-zinc-950 p-7 shadow-2xl shadow-black/60">
        <div className={`pointer-events-none absolute -top-24 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full blur-3xl ${premium ? 'bg-amber-500/20' : 'bg-emerald-500/20'}`} />

        <div className="relative">
          <div className={`mb-4 inline-flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ${premium ? 'bg-amber-500/15 ring-amber-500/30' : 'bg-emerald-500/15 ring-emerald-500/30'}`}>
            {premium ? <Clock className="h-6 w-6 text-amber-300" /> : <Sprout className="h-6 w-6 text-emerald-300" />}
          </div>

          {premium ? (
            <>
              <h2 className="text-2xl font-bold tracking-tight text-white">Premium unlocks the live feed</h2>
              <p className="mt-1.5 text-[15px] leading-relaxed text-zinc-400">This is a live, realtime surface. Add premium time to keep it streaming.</p>
              <ul className="mt-5 space-y-2.5">
                <Benefit>Keep your live alerts and watchlist running</Benefit>
                <Benefit>Full access to every scanner, chart, and feed</Benefit>
                <Benefit>Premium only counts down on market days, so a day pass goes far</Benefit>
              </ul>
              <div className="mt-6 space-y-2.5">
                <Link href="/subscribe" className="flex w-full items-center justify-center gap-2 rounded-xl bg-amber-500 px-4 py-3.5 text-[15px] font-bold text-black transition-colors hover:bg-amber-400">
                  Add premium time <ArrowRight className="h-4 w-4" />
                </Link>
                <DayPassCta />
                <Link prefetch={false} href="/donate" className="flex w-full items-center justify-center rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-300 transition-colors hover:bg-white/10">
                  Donate for premium time
                </Link>
              </div>
              <WallProof />
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold tracking-tight text-white">Create your free account</h2>
              <p className="mt-1.5 text-[15px] leading-relaxed text-zinc-400">Sign up to unlock this. It takes about 30 seconds, no card needed, and you get 5 free premium days.</p>
              <ul className="mt-5 space-y-2.5">
                <Benefit>See every alert the moment unusual volume hits</Benefit>
                <Benefit>Build your watchlist and get instant price alerts</Benefit>
                <Benefit>Open every scanner, chart, and live feed</Benefit>
              </ul>
              <div className="mt-6 space-y-2.5">
                {/* One-click Google sign-up — no page navigation. signIn returns them to this page. */}
                <button
                  type="button"
                  onClick={() => signIn('google', { callbackUrl: typeof window !== 'undefined' ? window.location.href : '/' })}
                  className="flex w-full items-center justify-center gap-2.5 rounded-xl bg-white px-4 py-3.5 text-[15px] font-bold text-zinc-900 shadow-sm transition-colors hover:bg-zinc-100"
                >
                  <GoogleG /> Continue with Google
                </button>
                <Link prefetch={false} href="/auth?mode=signup" className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-200 transition-colors hover:bg-white/10">
                  Sign up with email <ArrowRight className="h-4 w-4" />
                </Link>
                <Link prefetch={false} href="/auth" className="flex w-full items-center justify-center rounded-xl px-4 py-2 text-sm font-semibold text-zinc-400 transition-colors hover:text-zinc-200">
                  I already have an account
                </Link>
              </div>
              <p className="mt-4 text-center text-xs text-zinc-500">Free to start · 5 premium days · no card · join 800+ traders</p>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function GoogleG() {
  return (
    <svg className="h-[18px] w-[18px]" viewBox="0 0 24 24" aria-hidden="true">
      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23z" />
      <path fill="#FBBC05" d="M5.84 14.1a6.6 6.6 0 0 1 0-4.2V7.06H2.18a11 11 0 0 0 0 9.88l3.66-2.84z" />
      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84C6.71 7.31 9.14 5.38 12 5.38z" />
    </svg>
  )
}

/**
 * Real proof at the moment of highest purchase intent (the premium wall): the scanner's biggest recent
 * dated catches. Honest — real timestamped alerts, notable examples, not a profit promise. Renders
 * nothing until it has real wins, so the wall never shows an empty or fabricated state.
 */
type CatchRow = { symbol: string; dateLabel: string; alertPrice: number; high: number; runPct: number }
function WallProof() {
  const [rows, setRows] = useState<CatchRow[] | null>(null)
  useEffect(() => {
    let alive = true
    fetch('/api/caught-early?scope=recent', { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && Array.isArray(j?.rows)) setRows(j.rows.filter((x: CatchRow) => x.runPct >= 20).slice(0, 3)) })
      .catch(() => {})
    return () => { alive = false }
  }, [])
  if (!rows || rows.length === 0) return null
  const fmtPx = (n: number) => (n < 1 ? `$${n.toFixed(3)}` : `$${n.toFixed(2)}`)
  return (
    <div className="mt-6 rounded-2xl border border-emerald-500/20 bg-emerald-500/[0.04] p-3">
      <div className="mb-2 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-widest text-emerald-300/80">
        <TrendingUp className="h-3 w-3" /> Recently caught early
      </div>
      <div className="space-y-1.5">
        {rows.map((r) => (
          <div key={`${r.symbol}-${r.dateLabel}`} className="flex items-center gap-2 text-[12px]">
            <span className="font-bold text-white">${r.symbol}</span>
            <span className="text-zinc-500">{r.dateLabel}</span>
            <span className="truncate text-zinc-400">{fmtPx(r.alertPrice)} → {fmtPx(r.high)}</span>
            <span className="ml-auto shrink-0 rounded bg-emerald-500/15 px-1.5 py-0.5 text-[11px] font-bold tabular-nums text-emerald-300">+{r.runPct.toFixed(0)}%</span>
          </div>
        ))}
      </div>
      <p className="mt-2 text-[9px] leading-snug text-zinc-600">Real timestamped alerts. Not trade advice; past performance does not guarantee future results.</p>
    </div>
  )
}

/**
 * Low-commitment entry at the premium wall: a single-day pass. A day pass is a much smaller "yes" than
 * a monthly sub, so it converts skeptics at the highest-intent moment (and day-pass buyers frequently
 * upgrade). Price comes from PRICING (single source of truth, never hardcoded). Signed-in → start the
 * pass checkout directly; anonymous → route to /subscribe (which handles sign-in + the full selector).
 */
function DayPassCta() {
  const { data: session } = useSession()
  const [loading, setLoading] = useState(false)
  const dayRow = PRICING.pricingRows.find((r) => r.key === '1day')
  if (!dayRow) return null
  async function buy() {
    if (!session?.user) { window.location.href = '/subscribe'; return }
    setLoading(true)
    try {
      const res = await fetch('/api/stripe/create-pass', {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ plan: '1day' }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok || !data?.url) throw new Error('checkout')
      window.location.href = data.url
    } catch {
      window.location.href = '/subscribe' // graceful fallback to the full purchase view
    }
  }
  return (
    <button
      type="button" onClick={buy} disabled={loading}
      className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm font-semibold text-zinc-300 transition-colors hover:bg-white/10 disabled:opacity-60"
    >
      {loading ? 'Starting checkout…' : <>Just need today? Day pass - {formatUSD(dayRow.price)}</>}
    </button>
  )
}

function Benefit({ children }: { children: React.ReactNode }) {
  return (
    <li className="flex items-start gap-2.5 text-sm text-zinc-300">
      <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30">
        <Check className="h-3 w-3 text-emerald-300" />
      </span>
      <span className="leading-relaxed">{children}</span>
    </li>
  )
}
